/**
 * The Velora Modern side-nav layout (docs: ClaudeShare velora/ux/SIDEBAR-AUDIT.md).
 *
 *   Home
 *   PINNED            the user's own shortcuts (default: Schedule, Patients,
 *                     Charting, Imaging, Billing), in their order
 *   Front desk ...    collapsible categories in everyday-use order
 *   Communications / Patients / Clinical / Billing & insurance / Practice
 *   Not set up        (admins only, collapsed) modules whose integration is
 *                     not connected, each with a "Set up" link
 *   All modules       a browser of every module the role may open, where
 *                     hidden items come back
 *
 * MODULE-AUDIT 2026-10-20 section 5 (about 47 links down to about 16):
 *   - pages that are a tab one level down (Claims work queues, Scorecard,
 *     Goals, Close the day, Scan inbox, Referrals, ...) are MERGED: they
 *     leave the sidebar and open from their parent's tabs, All modules and
 *     Ctrl+K;
 *   - each role starts from its own pins, hidden items and collapsed
 *     categories (`roleDefaults`) until the person changes them;
 *   - locked add-ons and not-ready modules go to "Not set up" for people
 *     who can set them up, and are left out of everyone else's sidebar.
 *
 * Communications group (owner request 2026-10-20): everything that talks to
 * patients or prospects (texts and email, calls, leads, reminders, the AI
 * receptionist, online booking and portal requests, reviews, outreach) sits
 * in one group right after Front desk. It absorbed Growth, whose every item
 * was patient communication. Items not set up yet still wait in "Not set up"
 * and join Communications once connected (placement is by PLACEMENT, so
 * nothing extra decides where they land).
 *
 * Everything here is pure: `arrangeNav` turns the permission-filtered nav
 * entries, the user's saved layout and the integrations' connection states
 * into what the sidebar draws. The layout edits (`pin`, `hide`, ...) return
 * a new layout to save. The Familiar modes keep their own chrome and never
 * read this.
 */
import type { NavGroup as ManifestNavGroup } from '@velora/contracts';
import type { NavLayout } from '@velora/contracts/shell';
import type { IntegrationKey, IntegrationState } from '@velora/contracts/shell';

export type NavCategoryId =
  'frontdesk' | 'communications' | 'patients' | 'clinical' | 'billing' | 'practice';

export interface NavCategory {
  id: NavCategoryId;
  label: string;
  /** Lucide icon name (app/icons.tsx) for the collapsed rail. */
  icon: string;
}

/** Categories in everyday-use order (front desk work first, admin last). */
export const NAV_CATEGORIES: readonly NavCategory[] = [
  { id: 'frontdesk', label: 'Front desk', icon: 'calendar-days' },
  { id: 'communications', label: 'Communications', icon: 'message-square' },
  { id: 'patients', label: 'Patients', icon: 'users-round' },
  { id: 'clinical', label: 'Clinical', icon: 'smile' },
  { id: 'billing', label: 'Billing & insurance', icon: 'wallet' },
  { id: 'practice', label: 'Practice', icon: 'building-2' },
];

export const HOME_ID = 'home:dashboard';
/**
 * Right under Home, for whoever may open them (2026-11-03: the Office Manager
 * is its own top-level entry, not part of Analytics).
 */
export const TOP_IDS: readonly string[] = ['council:council'];
export const SETTINGS_ID = 'settings:home';

/**
 * Nav items Velora Modern does not draw. AI Assistant's Ask Velora is the
 * floating widget on every screen (modules/assistant/widget), so it has no
 * sidebar entry; the Familiar modes keep it in their own menus.
 */
export const WIDGET_ITEMS: ReadonlySet<string> = new Set(['assistant:ask']);

/** First-run pins: what every role opens many times a day. */
export const DEFAULT_PINS: readonly string[] = [
  'schedule:day',
  'patients:list',
  'chart:chart',
  'imaging:viewer',
  'ledger:billing',
];

/**
 * Where each nav item lives, in its category's order (most used first).
 * Items not listed fall back to their manifest group (`GROUP_FALLBACK`) and
 * sort after the listed ones, so a new module still appears somewhere.
 * Merged items (`MERGED_INTO`) are listed under their parent's category so
 * All modules shows them next to it.
 */
export const PLACEMENT: Readonly<Record<NavCategoryId, readonly string[]>> = {
  frontdesk: [
    'schedule:day',
    'schedule:offices',
    'schedule:calls',
    'gap-fill:openings',
    'schedule:productivity',
    'lightboard:board',
    'frontdesk:checkout',
    'frontdesk:close',
    'frontdesk:tasks',
    'forms:hub',
  ],
  /*
   * Phones carries the call log, missed calls (and missed-call texts), voicemail,
   * recordings, call analytics and AI review as its own tabs, so those are not
   * separate links. Online booking requests and portal requests are patients
   * writing in; Outreach absorbs the benefits campaign.
   */
  communications: [
    'inbox:threads',
    'phones:phones',
    'leads:board',
    'reminders:reminders',
    'receptionist:home',
    'booking:queue',
    'portal:home',
    'reviews:reviews',
    'campaigns:campaigns',
    'care-outreach:campaign',
  ],
  patients: [
    'patients:list',
    'patients:grades',
    'recall:workspace',
    'worklists:recall',
    'care-outreach:results',
    'frontdesk:patient',
    'frontdesk:scanInbox',
  ],
  clinical: [
    'chart:chart',
    'patient-brief:briefs',
    'imaging:viewer',
    'perio:worklist',
    'notes:due',
    'txplan:plans',
    'txplan:acceptance',
    'txplan:predeterminations',
    'case-closed:queue',
    'worklists:treatment',
    'lab:board',
    'referrals:list',
    // Owner feedback 2026-10-21: Prescriptions is its own Clinical module.
    'rx:rx',
    // Assistant role test (2026-10-20): turnover and room setup, sterilization, supplies.
    'chairside:turnover',
    'sterilization:log',
    'inventory:items',
  ],
  billing: [
    'ledger:billing',
    'worklists:ar',
    'claims:claims',
    'claims:work',
    'eligibility:worklist',
    // Owner feedback 2026-10-21: plans, carriers and fee schedules out of Settings.
    'insurance-plans:plans',
    'membership:membership',
  ],
  practice: [
    'analytics:boards',
    // Owner debrief 2026-10-28: the morning huddle is its own sidebar item.
    'analytics:huddle',
    // Hayden 2026-10-28 R10: the nightly day-end pack is a daily admin job.
    'analytics:day-end',
    // Owner research P0 (growth attribution): marketing source and ROI.
    'growth:roi',
    'analytics:scorecard',
    'analytics:goals',
    'analytics:schedule',
    'analytics:payer-contracts',
    'timeclock:timesheets',
    'radio:radio',
    'settings:home',
    'marketplace:store',
    'ai-usage:usage',
    'feedback:list',
    'help:center',
  ],
};

/**
 * Sidebar items that are a tab or section of another page (MODULE-AUDIT
 * "MERGE"): they leave the sidebar and open from the parent's tabs (the
 * shell's family tabs, Analytics and Claims tabs, the Settings rail), All
 * modules and Ctrl+K. Pinning one still shows it.
 */
export const MERGED_INTO: Readonly<Record<string, string>> = {
  'schedule:offices': 'schedule:day',
  'schedule:calls': 'schedule:day',
  // Schedule > Openings (gap fill): a tab of Schedule, next to Call list.
  'gap-fill:openings': 'schedule:day',
  'schedule:productivity': 'schedule:day',
  'lightboard:board': 'schedule:day',
  'frontdesk:close': 'frontdesk:checkout',
  'care-outreach:results': 'recall:workspace',
  'frontdesk:scanInbox': 'frontdesk:patient',
  'referrals:list': 'lab:board',
  'claims:work': 'claims:claims',
  'care-outreach:campaign': 'campaigns:campaigns',
  'analytics:scorecard': 'analytics:boards',
  'analytics:goals': 'analytics:boards',
  'analytics:schedule': 'analytics:boards',
  'analytics:payer-contracts': 'analytics:boards',
  // Release r8 (TC role test): tabs of Treatment plans.
  'txplan:acceptance': 'txplan:plans',
  'txplan:predeterminations': 'txplan:plans',
  // Case Closed (case acceptance follow-up and financing): a Treatment plans tab.
  'case-closed:queue': 'txplan:plans',
  'ai-usage:usage': 'settings:home',
};

/**
 * Always in the sidebar footer, in this order, never collapsed with a
 * category (Zac 2026-10-28: "Marketplace and Feature requests should always
 * be visible, just above Settings"). Each still needs the person's
 * permission; hiding one in the sidebar does not apply to these.
 */
export const FOOTER_IDS: readonly string[] = [
  'marketplace:store',
  // Help Center 2026-11-01: how-to guides one click away for everyone.
  'help:center',
  'feedback:list',
  SETTINGS_ID,
];

/** Sidebar names for items that absorbed another page. */
export const LABEL_OVERRIDE: Readonly<Record<string, string>> = {
  'lab:board': 'Lab & referrals',
  'campaigns:campaigns': 'Outreach',
};

/**
 * Built but not useful until the practice sets it up (MODULE-AUDIT "HIDE
 * until ready"); there is no connection check to ask. Admins find them in
 * "Not set up", everyone else in All modules.
 */
export const NOT_READY: Readonly<Record<string, { setupHref: string; detail: string }>> = {
  'portal:home': {
    setupHref: '/portal',
    detail: 'Turn the portal on and add the invite link to reminders before patients use it.',
  },
  'radio:radio': {
    setupHref: '/radio',
    detail: 'Needs a player in each room and at least one station.',
  },
};

const GROUP_FALLBACK: Readonly<Record<ManifestNavGroup, NavCategoryId>> = {
  dashboard: 'frontdesk',
  schedule: 'frontdesk',
  patients: 'patients',
  clinical: 'clinical',
  revenue: 'billing',
  operations: 'practice',
  analytics: 'practice',
  marketplace: 'practice',
  meta: 'practice',
  settings: 'practice',
};

/**
 * Nav items that only work once an outside service is connected. When the
 * integration reports `not_connected` the item leaves the main groups for
 * "Not set up". Anything else (connected, or the state is unknown because
 * the status call failed) keeps it where it is: a failed status call never
 * hides a working module.
 */
export const INTEGRATION_OF: Readonly<Record<string, IntegrationKey>> = {
  'booking:queue': 'online_booking',
  'phones:phones': 'phones',
  // Not 'reviews:reviews': Review Requests stays in the nav at every office
  // (Zac 2026-11-01); the feed says when Google is not connected.
  'receptionist:home': 'receptionist',
  // Its output is texts: with texting held it does nothing (rule 2). The
  // Communications inbox stays (history, portal messages, call notes).
  'campaigns:campaigns': 'texting',
  // Not 'reminders:reminders': its Unconfirmed list works by phone, so with
  // texting held it stays in the main nav with one banner on the page, like
  // Insurance (release r7 decision; front desk confirms by phone from it).
  // Not 'eligibility:worklist': Insurance stays in the main nav while the
  // clearinghouse is not connected: front desk verifies by phone or the payer
  // portal from that list (audit #9, role test FD-10).
};

/** One permission-filtered nav item, as the shell resolved it. */
export interface NavEntryIn {
  /** `<module id>:<route id>`. */
  id: string;
  moduleId: string;
  label: string;
  href: string;
  /** Manifest nav group (fallback placement). */
  group: ManifestNavGroup;
  /** Lucide icon name from the manifest. */
  icon: string;
  badge?: number;
  badgeTone?: 'neutral' | 'danger';
  /** A Marketplace module the offices in scope do not have (opens its upsell). */
  locked?: boolean;
  /** A Marketplace add-on (bought, trialling or comped): annotated in the nav. */
  addOn?: boolean;
  /** Hover text (useNavHint), e.g. "After-hours phone answering: On". */
  hint?: string;
}

/** Why an item sits in "Not set up". */
export type NotSetUpKind = IntegrationKey | 'addon' | 'not_ready';

export interface NotSetUpEntry<E extends NavEntryIn = NavEntryIn> {
  entry: E;
  integration: NotSetUpKind;
  setupHref: string;
  detail?: string;
}

export interface ArrangedCategory<E extends NavEntryIn = NavEntryIn> extends NavCategory {
  items: E[];
  collapsed: boolean;
}

export interface BrowserItem<E extends NavEntryIn = NavEntryIn> {
  entry: E;
  pinned: boolean;
  hidden: boolean;
  /** Integration not connected (or not ready): the item lives in "Not set up". */
  notSetUp: boolean;
  setupHref?: string;
  /** A tab of this sidebar item (MERGED_INTO): its label. */
  mergedInto?: string;
}

export interface ArrangedNav<E extends NavEntryIn = NavEntryIn> {
  home: E | undefined;
  /** Items right under Home (TOP_IDS), unless pinned. */
  top: E[];
  pinned: E[];
  /** Non-empty categories only (an emptied category collapses away). */
  categories: ArrangedCategory<E>[];
  /** Shown to people who can set integrations up; empty otherwise. */
  notSetUp: NotSetUpEntry<E>[];
  notSetUpOpen: boolean;
  /** Every category with every item the role may open, for "All modules". */
  browser: Array<NavCategory & { items: BrowserItem<E>[] }>;
  /** How many items the user has hidden (shown on the All modules entry). */
  hiddenCount: number;
  /** Fixed footer items (FOOTER_IDS) the person may open, in order. */
  footer: E[];
}

export interface IntegrationInfo {
  state: IntegrationState;
  setupHref: string;
  detail?: string;
}

/* ---------- role defaults ---------- */

/** The role a sidebar starts from (contracts identity ROLE_TEMPLATES keys). */
export type NavRole =
  | 'owner'
  | 'office_manager'
  | 'front_desk'
  | 'hygienist'
  | 'dentist'
  | 'assistant'
  | 'billing'
  | 'treatment_coordinator';

const ROLE_KEY: Record<string, NavRole> = {
  owner: 'owner',
  office_manager: 'office_manager',
  front_desk: 'front_desk',
  hygienist: 'hygienist',
  dentist: 'dentist',
  assistant: 'assistant',
  billing: 'billing',
  billing_admin: 'billing',
  treatment_coordinator: 'treatment_coordinator',
};

/** Several roles: the broadest wins. */
const ROLE_ORDER: readonly NavRole[] = [
  'owner',
  'office_manager',
  'dentist',
  'billing',
  'treatment_coordinator',
  'hygienist',
  'assistant',
  'front_desk',
];

/**
 * The person's sidebar role from their role keys, else (a custom role) a
 * guess from what they may do, else none (the generic defaults).
 */
export function navRoleOf(
  roleKeys: readonly string[],
  can?: (permission: string) => boolean,
): NavRole | undefined {
  const mapped = new Set(roleKeys.map((k) => ROLE_KEY[k]).filter(Boolean));
  const hit = ROLE_ORDER.find((r) => mapped.has(r));
  if (hit) return hit;
  if (!can) return undefined;
  if (can('settings.manage') && can('reports.view')) return 'office_manager';
  if (can('claims.send')) return 'billing';
  if (can('notes.sign')) return 'dentist';
  if (can('perio.edit')) return 'hygienist';
  if (can('schedule.edit')) return 'front_desk';
  return undefined;
}

/** What a sidebar starts with before the person changes anything. */
export interface NavDefaults {
  pins: readonly string[];
  hidden: readonly string[];
  collapsed: readonly NavCategoryId[];
}

/**
 * Hidden for everyone by default: AI usage lives in Settings. Marketplace
 * and Feature requests are footer items (FOOTER_IDS), always shown.
 */
const BASE_HIDDEN = ['ai-usage:usage'];
const ANALYTICS = ['analytics:boards'];
/** Prospect-facing Communications pages, out of clinical and billing sidebars. */
const GROWTH_OUT = ['leads:board', 'campaigns:campaigns', 'booking:queue'];
/** Billing and clinical roles: the Communications group starts collapsed. */
const COMMS_COLLAPSED: NavCategoryId[] = ['communications'];
/** Release r8 (assistant role test): back-of-house clinical pages, out of desk sidebars. */
const CHAIRSIDE_OUT = ['chairside:turnover', 'sterilization:log', 'inventory:items'];

/** MODULE-AUDIT section 5, "Modules to hide by default per role" and "Keep pinned". */
export const ROLE_DEFAULTS: Readonly<Record<NavRole, NavDefaults>> = {
  owner: {
    // The schedule is one click away for every role (familiar-nav 2026-10-29).
    pins: [
      'schedule:day',
      'analytics:huddle',
      'analytics:boards',
      'ledger:billing',
      'claims:claims',
    ],
    hidden: [...BASE_HIDDEN],
    // Everything operational collapsed; Communications, Billing and Practice open.
    collapsed: ['frontdesk', 'patients', 'clinical'],
  },
  office_manager: {
    pins: [
      'schedule:day',
      'analytics:huddle',
      'analytics:boards',
      'ledger:billing',
      'claims:claims',
    ],
    hidden: [...BASE_HIDDEN, 'radio:radio'],
    collapsed: [],
  },
  front_desk: {
    pins: [
      'schedule:day',
      'analytics:huddle',
      'patients:list',
      'frontdesk:checkout',
      // Communications group: the desk's three busiest conversation pages.
      'inbox:threads',
      'phones:phones',
      'leads:board',
      'eligibility:worklist',
    ],
    hidden: [
      ...BASE_HIDDEN,
      'perio:worklist',
      'notes:due',
      ...ANALYTICS,
      'timeclock:timesheets',
      'radio:radio',
      'membership:membership',
      ...CHAIRSIDE_OUT,
      'rx:rx',
    ],
    collapsed: [],
  },
  hygienist: {
    pins: ['schedule:day', 'perio:worklist', 'chart:chart', 'imaging:viewer', 'notes:due'],
    hidden: [...BASE_HIDDEN, ...GROWTH_OUT, 'forms:hub', 'reminders:reminders', 'radio:radio'],
    collapsed: [...COMMS_COLLAPSED],
  },
  dentist: {
    pins: [
      'schedule:day',
      'analytics:huddle',
      'chart:chart',
      'imaging:viewer',
      'notes:due',
      'txplan:plans',
    ],
    hidden: [
      ...BASE_HIDDEN,
      ...GROWTH_OUT,
      'reminders:reminders',
      'forms:hub',
      'timeclock:timesheets',
      'radio:radio',
    ],
    collapsed: [...COMMS_COLLAPSED],
  },
  assistant: {
    // Release r8 (assistant role test): Turnover is the assistant's page between patients.
    pins: ['schedule:day', 'chairside:turnover', 'chart:chart', 'imaging:viewer', 'lab:board'],
    hidden: [...BASE_HIDDEN, 'booking:queue', 'recall:workspace', 'forms:hub', 'radio:radio'],
    collapsed: [...COMMS_COLLAPSED],
  },
  billing: {
    pins: [
      'ledger:billing',
      'claims:claims',
      'eligibility:worklist',
      'frontdesk:tasks',
      'schedule:day',
    ],
    hidden: [
      ...BASE_HIDDEN,
      'perio:worklist',
      'notes:due',
      'imaging:viewer',
      'chart:chart',
      'lab:board',
      ...GROWTH_OUT,
      'radio:radio',
      ...CHAIRSIDE_OUT,
      'rx:rx',
    ],
    collapsed: [...COMMS_COLLAPSED],
  },
  treatment_coordinator: {
    pins: ['txplan:plans', 'schedule:day', 'patients:list', 'ledger:billing'],
    hidden: [...BASE_HIDDEN, 'perio:worklist', 'notes:due', 'radio:radio', ...CHAIRSIDE_OUT],
    collapsed: [...COMMS_COLLAPSED],
  },
};

/** No role known (tests, custom roles): the original pins, only the base hidden items. */
export const GENERIC_DEFAULTS: NavDefaults = {
  pins: DEFAULT_PINS,
  hidden: BASE_HIDDEN,
  collapsed: [],
};

export function navDefaults(role: NavRole | undefined): NavDefaults {
  return role ? ROLE_DEFAULTS[role] : GENERIC_DEFAULTS;
}

export interface ArrangeOptions {
  /** Integration states by key; a missing key counts as unknown (shown). */
  integrations?: Partial<Record<IntegrationKey, IntegrationInfo>> | undefined;
  /** The person may set integrations up (settings.manage): sees "Not set up". */
  canSetUp?: boolean;
  /** Pins, hidden items and collapsed categories before the person changes them. */
  defaults?: NavDefaults;
}

export function categoryOf(entry: Pick<NavEntryIn, 'id' | 'group'>): NavCategoryId {
  for (const c of NAV_CATEGORIES) if (PLACEMENT[c.id].includes(entry.id)) return c.id;
  return GROUP_FALLBACK[entry.group] ?? 'practice';
}

function rankIn(category: NavCategoryId, id: string): number {
  const i = PLACEMENT[category].indexOf(id);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

/** The pins in effect: the saved list, else the defaults. */
export function effectivePins(
  layout: NavLayout | undefined,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): readonly string[] {
  return layout?.pins ?? defaults.pins;
}

/** Hidden items in effect: the saved list, else the role's. */
export function effectiveHidden(
  layout: NavLayout | undefined,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): readonly string[] {
  return layout?.hidden ?? defaults.hidden;
}

/** Collapsed categories in effect: the saved list, else the role's. */
export function effectiveCollapsed(
  layout: NavLayout | undefined,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): readonly string[] {
  return layout?.collapsed ?? defaults.collapsed;
}

export function integrationFor(
  id: string,
  integrations: ArrangeOptions['integrations'],
): (IntegrationInfo & { key: IntegrationKey }) | undefined {
  const key = INTEGRATION_OF[id];
  if (!key) return undefined;
  const info = integrations?.[key];
  return info ? { ...info, key } : undefined;
}

/** Why `e` is not set up yet, or undefined when it works. */
function notSetUpOf<E extends NavEntryIn>(
  e: E,
  integrations: ArrangeOptions['integrations'],
): Omit<NotSetUpEntry<E>, 'entry'> | undefined {
  const integ = integrationFor(e.id, integrations);
  if (integ?.state === 'not_connected') {
    return {
      integration: integ.key,
      setupHref: integ.setupHref,
      ...(integ.detail ? { detail: integ.detail } : {}),
    };
  }
  // An add-on the offices in scope do not have: its page is the Marketplace
  // lock with the trial or turn-on sheet.
  if (e.locked) {
    return {
      integration: 'addon',
      setupHref: e.href,
      detail: 'Add-on, not on for these offices.',
    };
  }
  const ready = NOT_READY[e.id];
  if (ready) return { integration: 'not_ready', ...ready };
  return undefined;
}

const FOOTER: ReadonlySet<string> = new Set(FOOTER_IDS);

export function arrangeNav<E extends NavEntryIn>(
  allEntries: readonly E[],
  layout: NavLayout | undefined,
  opts: ArrangeOptions = {},
): ArrangedNav<E> {
  const defaults = opts.defaults ?? GENERIC_DEFAULTS;
  const entries = allEntries
    .filter((e) => !WIDGET_ITEMS.has(e.id))
    .map((e) => (LABEL_OVERRIDE[e.id] ? { ...e, label: LABEL_OVERRIDE[e.id] as string } : e));
  const byId = new Map(entries.map((e) => [e.id, e]));
  const hidden = new Set(effectiveHidden(layout, defaults));
  const collapsed = new Set(effectiveCollapsed(layout, defaults));
  const unset = new Map(
    entries.flatMap((e) => {
      const u = notSetUpOf(e, opts.integrations);
      return u ? [[e.id, u] as const] : [];
    }),
  );

  const home = byId.get(HOME_ID);
  const pinIds = effectivePins(layout, defaults).filter(
    (id, i, all) => all.indexOf(id) === i && id !== HOME_ID && byId.has(id) && !unset.has(id),
  );
  const pinned = pinIds.map((id) => byId.get(id) as E);
  const pinSet = new Set(pinIds);
  const top = TOP_IDS.filter((id) => byId.has(id) && !pinSet.has(id) && !hidden.has(id)).map(
    (id) => byId.get(id) as E,
  );
  const topSet = new Set(top.map((e) => e.id));

  const sorted = (cat: NavCategoryId, list: E[]) =>
    list.sort(
      (a, b) =>
        rankIn(cat, a.id) - rankIn(cat, b.id) ||
        entries.indexOf(a) - entries.indexOf(b) ||
        a.label.localeCompare(b.label),
    );

  const buckets = new Map<NavCategoryId, E[]>();
  const browserBuckets = new Map<NavCategoryId, BrowserItem<E>[]>();
  const notSetUp: NotSetUpEntry<E>[] = [];
  for (const e of entries) {
    if (e.id === HOME_ID) continue;
    const cat = categoryOf(e);
    const off = unset.get(e.id);
    const parent = MERGED_INTO[e.id];
    const parentLabel = parent ? byId.get(parent)?.label : undefined;
    const list = browserBuckets.get(cat) ?? [];
    list.push({
      entry: e,
      pinned: pinSet.has(e.id),
      hidden: hidden.has(e.id),
      notSetUp: off !== undefined,
      ...(off ? { setupHref: off.setupHref } : {}),
      ...(parentLabel ? { mergedInto: parentLabel } : {}),
    });
    browserBuckets.set(cat, list);
    if (off) {
      notSetUp.push({ entry: e, ...off });
      continue;
    }
    if (FOOTER.has(e.id) || topSet.has(e.id)) continue;
    if (pinSet.has(e.id) || hidden.has(e.id)) continue;
    // A tab of another sidebar item: only when that item is there to open it.
    if (parentLabel) continue;
    // Admin pages live in the Settings rail, the user menu or the sync chip;
    // for people without Settings they used to spill into Practice.
    if (e.group === 'settings' && e.id !== SETTINGS_ID) continue;
    const items = buckets.get(cat) ?? [];
    items.push(e);
    buckets.set(cat, items);
  }

  const categories: ArrangedCategory<E>[] = [];
  const browser: ArrangedNav<E>['browser'] = [];
  for (const c of NAV_CATEGORIES) {
    const items = buckets.get(c.id);
    if (items && items.length > 0) {
      categories.push({ ...c, items: sorted(c.id, items), collapsed: collapsed.has(c.id) });
    }
    const all = browserBuckets.get(c.id);
    if (all && all.length > 0) {
      const order = sorted(
        c.id,
        all.map((b) => b.entry),
      );
      browser.push({
        ...c,
        items: order.map((e) => all.find((b) => b.entry === e) as BrowserItem<E>),
      });
    }
  }

  return {
    home,
    top,
    pinned,
    categories,
    notSetUp: opts.canSetUp ? notSetUp : [],
    notSetUpOpen: layout?.notSetUpOpen === true,
    browser,
    hiddenCount: entries.filter((e) => hidden.has(e.id) && !unset.has(e.id) && !FOOTER.has(e.id))
      .length,
    footer: FOOTER_IDS.flatMap((id) => {
      const e = byId.get(id);
      return e && !unset.has(id) ? [e] : [];
    }),
  };
}

/* ---------- layout edits (each returns the layout to save) ---------- */

const without = (list: readonly string[] | undefined, id: string) =>
  (list ?? []).filter((x) => x !== id);

/** Pins `id` at the end of the pins; a hidden item becomes visible again. */
export function pin(
  layout: NavLayout | undefined,
  id: string,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  const pins = effectivePins(layout, defaults);
  return {
    ...layout,
    pins: pins.includes(id) ? [...pins] : [...pins, id],
    hidden: without(effectiveHidden(layout, defaults), id),
  };
}

/** Unpins `id`: it goes back to its category. */
export function unpin(
  layout: NavLayout | undefined,
  id: string,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  return { ...layout, pins: without(effectivePins(layout, defaults), id) };
}

/**
 * Moves a pin by `delta` places (Move up / Move down), clamped. `visible` is
 * the order the sidebar shows: saved pins the role cannot see (or that are
 * not set up) are skipped, so one step always moves past a visible pin.
 */
export function movePin(
  layout: NavLayout | undefined,
  id: string,
  delta: number,
  visible?: readonly string[],
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  const pins = [...effectivePins(layout, defaults)];
  const order = visible ? visible.filter((v) => pins.includes(v)) : pins;
  const from = order.indexOf(id);
  if (from === -1) return { ...layout, pins };
  const to = Math.max(0, Math.min(order.length - 1, from + delta));
  const target = order[to];
  if (to === from || target === undefined) return { ...layout, pins };
  return dropPin(layout, id, target, defaults);
}

/** Drops pin `id` at the place of pin `targetId` (drag and drop). */
export function dropPin(
  layout: NavLayout | undefined,
  id: string,
  targetId: string,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  const pins = [...effectivePins(layout, defaults)];
  const from = pins.indexOf(id);
  const to = pins.indexOf(targetId);
  if (from === -1 || to === -1 || from === to) return { ...layout, pins };
  pins.splice(from, 1);
  pins.splice(to, 0, id);
  return { ...layout, pins };
}

/** Hides `id` from the sidebar (and unpins it). "All modules" can show it again. */
export function hide(
  layout: NavLayout | undefined,
  id: string,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  const hidden = effectiveHidden(layout, defaults);
  return {
    ...layout,
    pins: without(effectivePins(layout, defaults), id),
    hidden: hidden.includes(id) ? [...hidden] : [...hidden, id],
  };
}

/** Shows a hidden item again in its category. */
export function show(
  layout: NavLayout | undefined,
  id: string,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  return { ...layout, hidden: without(effectiveHidden(layout, defaults), id) };
}

export function toggleCategory(
  layout: NavLayout | undefined,
  id: NavCategoryId,
  defaults: NavDefaults = GENERIC_DEFAULTS,
): NavLayout {
  const set = new Set(effectiveCollapsed(layout, defaults));
  if (set.has(id)) set.delete(id);
  else set.add(id);
  return { ...layout, collapsed: NAV_CATEGORIES.map((c) => c.id).filter((c) => set.has(c)) };
}

/** Back to the role's defaults: its pins, hidden items and collapsed categories. */
export function resetLayout(): NavLayout {
  return {};
}
